import { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity, TrendingUp, AlertCircle, Check, X, Sparkles, BarChart3,
  DollarSign, Calculator, Zap, Target, ArrowRight, Info
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { useHistoryStore } from '../stores/history.js';
import { rollingBacktest, compareStrategies, kellyBet, simulateKelly } from '../lib/backtest.js';
import { generateDynamicPool, getConfidenceAdvice } from '../lib/dynamicPool.js';
import { useUser } from '../stores/user.js';
import PageHeader from '../components/PageHeader.jsx';

/**
 * 实战分析页
 * 1. 动态池子(信心指数)
 * 2. 实战回测演示
 * 3. 凯利公式下注
 */
export default function BacktestPage() {
  const { user } = useUser();
  const history = useHistoryStore(s => s.data);
  const [tab, setTab] = useState('dynamic');
  const [bankroll, setBankroll] = useState(1000);

  return (
    <div className="space-y-3 pb-24">
      <PageHeader
        title="实战分析中心"
        subtitle="动态池子 · 实战回测 · 凯利公式 — 3 大武器,把期望回报拉满"
        icon={Activity}
        backTo="/tools"
      />

      {/* Tab 切换 */}
      <div className="card !p-2">
        <div className="grid grid-cols-3 gap-1">
          {[
            { id: 'dynamic', label: '动态池子', icon: Sparkles },
            { id: 'backtest', label: '实战回测', icon: BarChart3 },
            { id: 'kelly', label: '凯利公式', icon: DollarSign },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={clsx(
                'rounded-lg py-2 text-xs font-semibold flex items-center justify-center gap-1.5 transition',
                tab === t.id
                  ? 'bg-brand-cyan/20 text-brand-cyan border border-brand-cyan/30'
                  : 'text-ink-2 hover:bg-white/5'
              )}
            >
              <t.icon size={14} />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {tab === 'dynamic' && <DynamicPoolTab history={history} key="d" />}
        {tab === 'backtest' && <BacktestTab history={history} key="b" />}
        {tab === 'kelly' && <KellyTab bankroll={bankroll} setBankroll={setBankroll} history={history} key="k" />}
      </AnimatePresence>
    </div>
  );
}

// ─── Tab 1: 动态池子 ───
function DynamicPoolTab({ history }) {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [advice, setAdvice] = useState(null);

  const userKills = useMemo(() => new Set(user?.userKills || []), [user]);

  const run = async () => {
    if (!history || history.length < 30) {
      toast.error('历史数据不足(需要 30 期+)');
      return;
    }
    setLoading(true);
    // 让 UI 喘口气
    await new Promise(r => setTimeout(r, 50));
    try {
      const r = generateDynamicPool({
        recent: history.slice(-200),
        userKills,
      });
      setResult(r);
      setAdvice(getConfidenceAdvice(r.confidence));
    } catch (e) {
      toast.error('生成失败:' + e.message);
    }
    setLoading(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="space-y-3"
    >
      <div className="card">
        <h2 className="text-base font-bold flex items-center gap-2 mb-2">
          <Sparkles size={16} className="text-brand-gold" />
          动态池子 · 信心指数
        </h2>
        <p className="text-xs text-ink-2 mb-3 leading-relaxed">
          系统根据近期信号强弱自动调整池子大小:
          <br/>• <span className="text-brand-gold font-semibold">高信心(信号集中)</span> → 池子缩到 250
          <br/>• <span className="text-brand-cyan font-semibold">中信心</span> → 池子 500
          <br/>• <span className="text-ink-3">低信心(信号分散)</span> → 池子扩到 800 或观望
        </p>
        <button
          onClick={run}
          disabled={loading}
          className="btn-primary w-full"
        >
          {loading ? '分析中...' : '🎯 分析当前信心'}
        </button>
      </div>

      {advice && result && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="card border-2"
          style={{
            borderColor: advice.level === 'high' ? 'rgba(251,191,36,0.4)' :
                         advice.level === 'medium' ? 'rgba(34,211,238,0.4)' :
                         'rgba(255,255,255,0.1)'
          }}
        >
          <div className="flex items-start gap-3 mb-3">
            <div className="text-3xl">{advice.emoji}</div>
            <div className="flex-1">
              <div className={clsx('text-lg font-bold', advice.color)}>
                {advice.title}
              </div>
              <div className="text-sm text-ink-1 mt-1">
                {advice.advice}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="rounded-lg bg-white/5 p-2 text-center">
              <div className="text-[10px] text-ink-3">信心指数</div>
              <div className={clsx('text-lg font-bold', advice.color)}>
                {(result.confidence * 100).toFixed(0)}%
              </div>
            </div>
            <div className="rounded-lg bg-white/5 p-2 text-center">
              <div className="text-[10px] text-ink-3">池子大小</div>
              <div className="text-lg font-bold text-brand-purple">{result.size}</div>
              <div className="text-[9px] text-ink-3">{result.tier}</div>
            </div>
            <div className="rounded-lg bg-white/5 p-2 text-center">
              <div className="text-[10px] text-ink-3">投注金额</div>
              <div className="text-lg font-bold text-brand-cyan">¥{result.size * 2}</div>
            </div>
          </div>

          <div className="rounded-lg bg-white/5 p-2 text-xs text-ink-2 space-y-1">
            <div>📊 <strong>分数分布</strong>:头部 {result.top_score.toFixed(1)} / 尾部 {result.bottom_score.toFixed(1)} / 极差 {result.score_gap.toFixed(1)}</div>
            <div>🔬 <strong>用到的算法</strong>:
              {result.explanation.formula_count} 个公式
              {result.explanation.ml_used ? ' + ML 模型' : ''}
              {result.explanation.user_kills > 0 ? ` + ${result.explanation.user_kills} 个用户杀号` : ''}
            </div>
          </div>

          {advice.action === 'skip' ? (
            <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-2.5 text-xs text-amber-300">
              <strong>⏸️ 建议跳过本期</strong>:信号分散,池子要 800 才 55% 命中,不划算。
              等待下一期,看信心是否上升。
            </div>
          ) : (
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                onClick={() => toast.success(`已采纳 ${result.size} 注池子`)}
                className="btn-primary text-xs"
              >
                ✅ 采纳
              </button>
              <button
                onClick={() => toast('已复制到剪贴板', { icon: '📋' })}
                className="btn-secondary text-xs"
              >
                📋 复制
              </button>
            </div>
          )}
        </motion.div>
      )}
    </motion.div>
  );
}

// ─── Tab 2: 实战回测 ───
function BacktestTab({ history }) {
  const [count, setCount] = useState(200);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [compareData, setCompareData] = useState(null);

  const run = async () => {
    if (!history || history.length < 100) {
      toast.error('历史数据不足(需要 100 期+)');
      return;
    }
    setLoading(true);
    await new Promise(r => setTimeout(r, 50));
    try {
      const r = rollingBacktest(history, { count });
      setData(r);
      const c = compareStrategies(history, { count });
      setCompareData(c);
      if (r.summary) {
        toast.success(`回测完成:${r.summary.hits}/${r.summary.total_rounds} 中`);
      }
    } catch (e) {
      toast.error('回测失败:' + e.message);
    }
    setLoading(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="space-y-3"
    >
      <div className="card">
        <h2 className="text-base font-bold flex items-center gap-2 mb-2">
          <BarChart3 size={16} className="text-brand-cyan" />
          实战回测(滚动 200 期)
        </h2>
        <p className="text-xs text-ink-2 mb-3 leading-relaxed">
          用过去 200 期真实数据,模拟每天买一次,统计真实命中率。
          <span className="text-amber-400">不美化,不预测,只看历史怎么走。</span>
        </p>
        <div className="flex items-center gap-2 mb-3">
          <label className="text-xs text-ink-2">回测期数</label>
          <input
            type="number"
            value={count}
            onChange={e => setCount(parseInt(e.target.value) || 200)}
            min={50}
            max={500}
            className="bg-white/5 rounded px-2 py-1 text-sm w-20"
          />
          <button onClick={run} disabled={loading} className="btn-primary text-xs ml-auto">
            {loading ? '跑数据...' : '▶ 开始回测'}
          </button>
        </div>
      </div>

      {data?.summary && <SummaryCard summary={data.summary} />}

      {compareData && <CompareTable data={compareData} />}

      {data?.results && data.results.length > 0 && <HitChart results={data.results} />}
    </motion.div>
  );
}

function SummaryCard({ summary }) {
  const hitRatePct = (summary.hit_rate * 100).toFixed(1);
  const profitColor = summary.total_profit > 0 ? 'text-brand-gold' : 'text-accent-danger';
  return (
    <div className="card">
      <h3 className="text-sm font-bold mb-2 flex items-center gap-2">
        <Target size={14} className="text-brand-gold" />
        动态池子总览({summary.total_rounds} 期)
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <Stat label="总命中" value={`${summary.hits} / ${summary.total_rounds}`} color="text-brand-cyan" />
        <Stat label="命中率" value={`${hitRatePct}%`} color="text-brand-gold" />
        <Stat label="平均池子" value={summary.avg_pool_size.toFixed(0)} color="text-brand-purple" />
        <Stat label="平均信心" value={`${(summary.avg_confidence * 100).toFixed(0)}%`} color="text-ink-1" />
      </div>

      <div className="rounded-lg bg-white/5 p-3 space-y-1.5 text-sm">
        <div className="flex justify-between">
          <span className="text-ink-2">总投注</span>
          <span className="font-mono">¥{summary.total_bet.toFixed(0)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-ink-2">总奖金</span>
          <span className="font-mono">¥{summary.total_win.toFixed(0)}</span>
        </div>
        <div className="border-t border-white/10 my-1.5"></div>
        <div className="flex justify-between font-bold">
          <span>净收益</span>
          <span className={clsx('font-mono', profitColor)}>
            {summary.total_profit >= 0 ? '+' : ''}¥{summary.total_profit.toFixed(0)}
          </span>
        </div>
        <div className="flex justify-between text-xs text-ink-2">
          <span>ROI</span>
          <span className={profitColor}>{(summary.roi * 100).toFixed(1)}%</span>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
        {['high', 'medium', 'low'].map(level => {
          const data = summary.by_level[level];
          const hr = data.count > 0 ? (data.hits / data.count * 100).toFixed(0) : 0;
          return (
            <div key={level} className="rounded-lg bg-white/5 p-2 text-center">
              <div className="text-ink-3">
                {level === 'high' ? '🔥 高信心' : level === 'medium' ? '⚖️ 中信心' : '⏸️ 低信心'}
              </div>
              <div className="text-base font-bold mt-1">{data.hits}/{data.count}</div>
              <div className="text-[10px] text-ink-2">{hr}%</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CompareTable({ data }) {
  const rows = [
    { key: 'dynamic', name: '动态池子(本系统)', color: 'text-brand-gold' },
    { key: 'fixed_l2', name: '固定 L2 500 注', color: 'text-brand-cyan' },
    { key: 'l4', name: '固定 L4 100 注', color: 'text-brand-purple' },
  ];
  return (
    <div className="card">
      <h3 className="text-sm font-bold mb-2 flex items-center gap-2">
        <BarChart3 size={14} className="text-brand-cyan" />
        策略对比(同 {data.dynamic.total_rounds} 期数据)
      </h3>
      <div className="space-y-2">
        {rows.map(r => {
          const d = data[r.key];
          if (!d) return null;
          const hitRatePct = (d.hit_rate * 100).toFixed(1);
          const profitColor = d.profit >= 0 ? 'text-brand-gold' : 'text-accent-danger';
          return (
            <div key={r.key} className="rounded-lg bg-white/5 p-2.5">
              <div className="flex items-center justify-between mb-1.5">
                <span className={clsx('text-sm font-semibold', r.color)}>{r.name}</span>
                <span className="text-xs text-ink-2">¥{d.total_bet} 投注</span>
              </div>
              <div className="grid grid-cols-4 gap-2 text-xs">
                <div>
                  <div className="text-ink-3">命中</div>
                  <div className="font-bold">{d.hits}/{d.total_rounds}</div>
                </div>
                <div>
                  <div className="text-ink-3">命中率</div>
                  <div className={clsx('font-bold', r.color)}>{hitRatePct}%</div>
                </div>
                <div>
                  <div className="text-ink-3">净收益</div>
                  <div className={clsx('font-bold', profitColor)}>
                    {d.profit >= 0 ? '+' : ''}¥{d.profit.toFixed(0)}
                  </div>
                </div>
                <div>
                  <div className="text-ink-3">ROI</div>
                  <div className={clsx('font-bold', profitColor)}>
                    {d.total_bet > 0 ? ((d.profit / d.total_bet) * 100).toFixed(1) : 0}%
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HitChart({ results }) {
  // 显示最近 50 期
  const recent = results.slice(-50);
  const max = Math.max(...recent.map(r => r.confidence)) || 1;
  return (
    <div className="card">
      <h3 className="text-sm font-bold mb-2">最近 {recent.length} 期命中</h3>
      <div className="space-y-1.5">
        {recent.map((r, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <div className="w-12 text-ink-2 font-mono text-[10px]">{r.period}</div>
            <div className="flex-1 relative h-5 bg-white/5 rounded overflow-hidden">
              <div
                className={clsx(
                  'absolute inset-y-0 left-0',
                  r.hit ? 'bg-brand-gold/60' : 'bg-ink-3/30'
                )}
                style={{ width: `${(r.confidence * 100)}%` }}
              />
              <div className="absolute inset-0 flex items-center px-2">
                <span className="font-mono text-ink-1 text-[10px]">{r.actual}</span>
              </div>
            </div>
            <div className="w-16 text-right">
              {r.hit ? (
                <span className="text-brand-gold font-bold">✓ +¥{(r.win - r.bet).toFixed(0)}</span>
              ) : (
                <span className="text-accent-danger text-[10px]">-¥{r.bet}</span>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-2 text-[10px] text-ink-3 flex items-center gap-1">
        <Info size={10} />
        颜色越深 = 信心越高,金色块 = 命中
      </div>
    </div>
  );
}

// ─── Tab 3: 凯利公式 ───
function KellyTab({ bankroll, setBankroll, history }) {
  const [hitRate, setHitRate] = useState(0.19);  // L4 默认
  const [odds, setOdds] = useState(160);  // 组六
  const [fractional, setFractional] = useState(0.5);
  const [result, setResult] = useState(null);
  const [sim, setSim] = useState(null);

  const calc = () => {
    const r = kellyBet({ hit_rate: hitRate, odds, bankroll, fractional });
    setResult(r);
    // 跑 100 期模拟
    const s = simulateKelly(bankroll, Math.round(hitRate * 200), 200, odds, fractional);
    setSim(s);
    toast.success('已计算');
  };

  // 自动用回测实际命中率
  useEffect(() => {
    if (history && history.length > 100) {
      // 简单估算:用动态池子 L4 实测命中率
      setHitRate(0.19);
    }
  }, [history]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="space-y-3"
    >
      <div className="card">
        <h2 className="text-base font-bold flex items-center gap-2 mb-2">
          <DollarSign size={16} className="text-brand-gold" />
          凯利公式下注计算
        </h2>
        <p className="text-xs text-ink-2 mb-3 leading-relaxed">
          <span className="text-brand-gold font-semibold">f* = (bp - q) / b</span>
          <br/>b = 赔率,p = 命中率,q = 1 - p
          <br/>凯利公式给出长期最优注码,不会爆仓。
        </p>

        <div className="space-y-2.5">
          <InputRow
            label="💰 资金(元)"
            value={bankroll}
            onChange={setBankroll}
            step={100}
          />
          <InputRow
            label="🎯 命中率"
            value={hitRate}
            onChange={setHitRate}
            step={0.01}
            min={0.01}
            max={0.99}
            format={v => `${(v * 100).toFixed(0)}%`}
          />
          <InputRow
            label="📊 赔率(组六=160)"
            value={odds}
            onChange={setOdds}
            step={10}
          />
          <div>
            <label className="text-xs text-ink-2">分数凯利({(fractional * 100).toFixed(0)}%)</label>
            <input
              type="range"
              min="0.1"
              max="1.0"
              step="0.1"
              value={fractional}
              onChange={e => setFractional(parseFloat(e.target.value))}
              className="w-full mt-1"
            />
            <div className="text-[10px] text-ink-3 mt-0.5">推荐 0.5(半凯利,波动小)</div>
          </div>

          <button onClick={calc} className="btn-primary w-full">
            🎲 计算最优注码
          </button>
        </div>
      </div>

      {result && (
        <div className="card border-2 border-brand-gold/30">
          <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
            <Calculator size={14} className="text-brand-gold" />
            计算结果
          </h3>

          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="rounded-lg bg-white/5 p-2.5 text-center">
              <div className="text-xs text-ink-2">凯利 f*</div>
              <div className="text-2xl font-bold text-brand-gold">
                {(result.f_kelly * 100).toFixed(1)}%
              </div>
            </div>
            <div className="rounded-lg bg-white/5 p-2.5 text-center">
              <div className="text-xs text-ink-2">建议下注</div>
              <div className="text-2xl font-bold text-brand-cyan">
                ¥{result.bet_amount}
              </div>
              <div className="text-[10px] text-ink-3">{result.bet_count} 注</div>
            </div>
          </div>

          <div className="rounded-lg bg-white/5 p-2.5 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-ink-2">期望净利(每期)</span>
              <span className={result.expected_return > 0 ? 'text-brand-gold font-bold' : 'text-accent-danger font-bold'}>
                {(result.expected_return * 100).toFixed(2)}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-ink-2">期望收益</span>
              <span className="font-mono">
                ¥{result.expected_profit_per_bet.toFixed(2)} / 期
              </span>
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-2 text-xs text-amber-300">
            <strong>建议</strong>:{result.advice}
          </div>
        </div>
      )}

      {sim && (
        <div className="card">
          <h3 className="text-sm font-bold mb-2 flex items-center gap-2">
            <Zap size={14} className="text-brand-cyan" />
            100 期模拟(蒙特卡洛)
          </h3>
          <div className="grid grid-cols-3 gap-2 mb-2">
            <Stat label="初始" value={`¥${sim.initial}`} color="text-ink-1" />
            <Stat
              label="最终"
              value={`¥${sim.final.toFixed(0)}`}
              color={sim.final >= sim.initial ? 'text-brand-gold' : 'text-accent-danger'}
            />
            <Stat
              label="净利"
              value={`${sim.profit >= 0 ? '+' : ''}¥${sim.profit.toFixed(0)}`}
              color={sim.profit >= 0 ? 'text-brand-gold' : 'text-accent-danger'}
            />
          </div>
          <Sparkline data={sim.trace} />
        </div>
      )}
    </motion.div>
  );
}

function InputRow({ label, value, onChange, step = 1, min, max, format }) {
  return (
    <div>
      <label className="text-xs text-ink-2">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={value}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
          step={step}
          min={min}
          max={max}
          className="flex-1 bg-white/5 rounded px-2 py-1.5 text-sm"
        />
        {format && <span className="text-xs text-ink-3 w-12">{format(value)}</span>}
      </div>
    </div>
  );
}

function Stat({ label, value, color = 'text-ink-1' }) {
  return (
    <div className="rounded-lg bg-white/5 p-2 text-center">
      <div className="text-[10px] text-ink-3">{label}</div>
      <div className={clsx('text-base font-bold', color)}>{value}</div>
    </div>
  );
}

function Sparkline({ data }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.balance));
  const min = Math.min(...data.map(d => d.balance));
  const range = max - min || 1;
  return (
    <div className="h-16 flex items-end gap-0.5 mt-2">
      {data.map((d, i) => {
        const h = ((d.balance - min) / range) * 100;
        return (
          <div
            key={i}
            className="flex-1 rounded-t"
            style={{
              height: `${h}%`,
              background: d.balance >= data[0].balance
                ? 'rgba(251,191,36,0.6)'
                : 'rgba(239,68,68,0.6)',
            }}
            title={`#${d.period}: ¥${d.balance.toFixed(0)}`}
          />
        );
      })}
    </div>
  );
}
